/**
 * VueUseのuseAxiosフックをインポート
 * HTTPリクエストの実行と状態管理を提供する
 */
import { useAxios } from "@vueuse/integrations/useAxios";

/**
 * APIルーター定数をインポート
 * 手動タグ関連のAPIエンドポイントURLを提供する
 */
import ApiRouterConstants from "@/clinic/constants/api.router.constants";

/**
 * HTTP通信の共通ユーティリティをインポート
 * Axiosインスタンスと共通設定を提供する
 */
import http from "@/common/utils/http-common-utils";

/**
 * 手動タグ関連の型定義をインポート
 * APIリクエスト・レスポンスの型を提供する
 */
import type { ApiManualTagIndexReq, ApiManualTagReq } from "@/clinic/typings";

/**
 * 手動タグ管理のためのAPIサービス
 * タグの取得、作成、更新、削除の操作を提供する
 */
const ManualTagServices = {
  /**
   * 手動タグ一覧を取得する
   * @param {ApiManualTagIndexReq} params - タグ取得のためのリクエストパラメータ
   * @param {() => void} onSuccess - 成功時のコールバック関数
   * @param {() => void} onError - エラー時のコールバック関数
   * @param {() => void} onFinish - 処理完了時のコールバック関数
   * @returns {Promise<any>} APIレスポンス
   */
  fetchTags: async (params: ApiManualTagIndexReq, onSuccess: () => void, onError: () => void, onFinish: () => void) => {
    const { execute } = useAxios(ApiRouterConstants.API_MANUAL_TAGS, { method: "GET", params: params }, http, {
      immediate: false,
      onSuccess: () => {
        onSuccess();
      },
      onError: () => {
        onError();
      },
      onFinish: () => {
        onFinish();
      },
    });
    return await execute();
  },

  /**
   * 手動タグを削除する
   * @param {ApiManualTagReq} params - 削除対象のタグ情報（id, clinic_id）
   * @param {() => void} onSuccess - 成功時のコールバック関数
   * @param {() => void} onError - エラー時のコールバック関数
   * @returns {Promise<any>} APIレスポンス
   */
  deleteTag: async (params: ApiManualTagReq, onSuccess: () => void, onError: () => void) => {
    const { execute } = useAxios(
      `${ApiRouterConstants.API_MANUAL_TAGS}/${params.id}`,
      { method: "DELETE", params: { clinic_id: params.clinic_id } },
      http,
      {
        immediate: false,
        onSuccess: () => {
          onSuccess();
        },
        onError: () => {
          onError();
        },
      }
    );
    return await execute();
  },

  /**
   * 手動タグを保存する（作成または更新）
   * @param {any} formObject - 保存するタグのフォームデータ
   * @param {() => void} onSuccess - 成功時のコールバック関数
   * @param {(errors: any) => void} onError - エラー時のコールバック関数（エラー情報を受け取る）
   * @returns {Promise<any>} APIレスポンス
   */
  saveTag: async (formObject: any, onSuccess: () => void, onError: (errors: any) => void) => {
    // 作成か更新かを判定（idの有無で判断）
    const method = formObject.id ? "PATCH" : "POST";
    const url = formObject.id
      ? `${ApiRouterConstants.API_MANUAL_TAGS}/${formObject.id}`
      : ApiRouterConstants.API_MANUAL_TAGS;

    // リクエストの設定
    const requestConfig: any = { method: method };

    // 作成時と更新時の両方でclinic_idをクエリパラメータに含める
    requestConfig.params = { clinic_id: formObject.clinic_id };

    const { execute } = useAxios(url, requestConfig, http, {
      immediate: false,
      onSuccess: () => {
        onSuccess();
      },
      onError: (errors: any) => {
        onError(errors.response?.data);
      },
    });

    // リクエストボディのデータを準備
    let requestData;
    if (formObject.id) {
      // 更新時は必要なデータのみをリクエストボディに含める
      requestData = {
        name: formObject.name,
        tag_category_id: formObject.tag_category_id,
      };
    } else {
      // 作成時は必要なデータのみをリクエストボディに含める
      requestData = {
        name: formObject.name,
        tag_category_id: formObject.tag_category_id,
      };
    }

    return await execute({ data: requestData });
  },
};

export default ManualTagServices;
